import type {
  CalibrationPageData,
  InspectionTechnique,
} from 'pages/reporting/reports/raut/rautReportData'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import type { ParsedCalibrationAutofill } from './types'

const MICROSECONDS_PER_SECOND = 1_000_000

const buildTechnique = (
  existingTechnique: InspectionTechnique | undefined,
  parsedTechnique: ParsedCalibrationAutofill['fields']['techniques'][number],
  geometricUnit: GeckoMeasurementUnit
): InspectionTechnique => {
  const pitch =
    parsedTechnique.pitchMeters === undefined
      ? undefined
      : GeckoMeasurement.CreateFromValue(
          parsedTechnique.pitchMeters,
          GeckoMeasurementUnit.Meters
        )
          .convertTo(geometricUnit)
          .round(3)

  const velocity =
    parsedTechnique.velocityMetersPerSecond === undefined
      ? undefined
      : GeckoMeasurement.CreateFromValue(
          parsedTechnique.velocityMetersPerSecond / MICROSECONDS_PER_SECOND,
          GeckoMeasurementUnit.Meters
        )
          .convertTo(geometricUnit)
          .round(4)

  return {
    ...existingTechnique,
    id: existingTechnique?.id ?? crypto.randomUUID(),
    techniqueName:
      parsedTechnique.techniqueName as InspectionTechnique['techniqueName'],
    waveMode: parsedTechnique.waveMode,
    probePartNumber: parsedTechnique.probePartNumber,
    frequency: parsedTechnique.frequencyMHz,
    elementQuantity: parsedTechnique.elementQuantity,
    pitch,
    angle: parsedTechnique.angle,
    scanningGain: parsedTechnique.referenceGain,
    velocity,
  }
}

export const mapParsedNdeToCalibration = (
  existingCalibration: CalibrationPageData,
  parsedCalibration: ParsedCalibrationAutofill,
  geometricUnit: GeckoMeasurementUnit
): CalibrationPageData => {
  const mergedTechniques = parsedCalibration.fields.techniques.map(
    (parsedTechnique, index) =>
      buildTechnique(
        existingCalibration.techniques?.[index],
        parsedTechnique,
        geometricUnit
      )
  )

  const xMotorScanRes =
    parsedCalibration.fields.xMotor?.scanResolutionMeters === undefined
      ? undefined
      : GeckoMeasurement.CreateFromValue(
          parsedCalibration.fields.xMotor.scanResolutionMeters,
          GeckoMeasurementUnit.Meters
        )
          .convertTo(geometricUnit)
          .round(3)

  const yMotorScanRes =
    parsedCalibration.fields.yMotor?.scanResolutionMeters === undefined
      ? undefined
      : GeckoMeasurement.CreateFromValue(
          parsedCalibration.fields.yMotor.scanResolutionMeters,
          GeckoMeasurementUnit.Meters
        )
          .convertTo(geometricUnit)
          .round(3)

  const calibrationData: CalibrationPageData = {
    ...existingCalibration,
    testingUnit: parsedCalibration.fields.testingUnit,
    model: parsedCalibration.fields.model,
    instrumentSN: parsedCalibration.fields.instrumentSN,
    techniques: mergedTechniques,
    xMotor: {
      ...(existingCalibration.xMotor || {}),
      function: parsedCalibration.fields.xMotor?.function,
      scanRes: xMotorScanRes,
    },
    yMotor: {
      ...(existingCalibration.yMotor || {}),
      function: parsedCalibration.fields.yMotor?.function,
      scanRes: yMotorScanRes,
    },
  }

  return calibrationData
}
